import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { SrIcon } from "@/components/ui/SrIcon";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import type { DBFreelancerDiscipline } from "@/lib/db-types";
import type { FreelancerFilters } from "@/lib/freelancers";

interface Props {
  filters: FreelancerFilters;
  onChange: (patch: Partial<FreelancerFilters>) => void;
  disciplines: DBFreelancerDiscipline[];
}

const ALL = "all";

export function FreelancerListToolbar({ filters, onChange, disciplines }: Props) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="relative min-w-[16rem] flex-1">
        <SrIcon name="search" className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={filters.search}
          onChange={(e) => onChange({ search: e.target.value })}
          placeholder="Søk på navn, selskap, sted eller kjennetegn …"
          className="pl-8"
          aria-label="Søk i frilansere"
        />
      </div>
      <Select
        value={filters.disciplineIds[0] ?? ALL}
        onValueChange={(v) => onChange({ disciplineIds: v === ALL ? [] : [v] })}
      >
        <SelectTrigger className="w-48" aria-label="Filtrer på fagfelt">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>Alle fagfelt</SelectItem>
          {disciplines.filter((d) => d.is_active).map((d) => (
            <SelectItem key={d.id} value={d.id}>{d.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select value={filters.kind} onValueChange={(v) => onChange({ kind: v as FreelancerFilters["kind"] })}>
        <SelectTrigger className="w-40" aria-label="Filtrer på person eller firma">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Person og firma</SelectItem>
          <SelectItem value="person">Bare personer</SelectItem>
          <SelectItem value="company">Bare firma</SelectItem>
        </SelectContent>
      </Select>
      <Select value={String(filters.minRating)} onValueChange={(v) => onChange({ minRating: Number(v) })}>
        <SelectTrigger className="w-40" aria-label="Filtrer på vurdering">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="0">Alle vurderinger</SelectItem>
          <SelectItem value="4">4 stjerner eller mer</SelectItem>
          <SelectItem value="3">3 stjerner eller mer</SelectItem>
        </SelectContent>
      </Select>
      <Select value={filters.tested} onValueChange={(v) => onChange({ tested: v as FreelancerFilters["tested"] })}>
        <SelectTrigger className="w-44" aria-label="Filtrer på testet">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Testet og ikke testet</SelectItem>
          <SelectItem value="tested">Bare testede</SelectItem>
          <SelectItem value="untested">Bare ikke testet</SelectItem>
        </SelectContent>
      </Select>
      <div className="flex items-center gap-2">
        <Switch
          id="freelancers-show-archived"
          checked={filters.showArchived}
          onCheckedChange={(checked) => onChange({ showArchived: checked })}
        />
        <Label htmlFor="freelancers-show-archived" className="text-sm text-muted-foreground">Vis arkiverte</Label>
      </div>
    </div>
  );
}
